import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/currency";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, Banknote, ShoppingBag, TrendingUp, Receipt, Trophy } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <p className="font-mono-j text-3xl font-bold mt-2">{value}</p>
  </div>
);

export const InsightsDashboard = ({ accent = "#F97316", currency }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my/insights").then((r) => setData(r.data));
  }, []);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-orange-500" />;

  const maxQty = Math.max(1, ...data.top_items.map((i) => i.qty));

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl font-bold">Sales Insights</h2>
        <p className="text-slate-400 text-sm">How your restaurant is performing at a glance.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Banknote} label="Revenue today" value={money(data.today_revenue, currency)} testid="stat-today-revenue" />
        <StatCard icon={ShoppingBag} label="Orders today" value={data.today_orders} testid="stat-today-orders" />
        <StatCard icon={Receipt} label="Avg order value" value={money(data.avg_order_value, currency)} testid="stat-avg-order" />
        <StatCard icon={TrendingUp} label="Total revenue" value={money(data.total_revenue, currency)} testid="stat-total-revenue" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-sub font-semibold text-lg mb-5">Revenue — last 7 days</h3>
          <div className="h-64" data-testid="revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.revenue_7d} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94A3B8" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip
                  cursor={{ fill: "#1e293b" }}
                  contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 12, color: "#f8fafc" }}
                  formatter={(v) => [money(v, currency), "Revenue"]}
                />
                <Bar dataKey="revenue" fill={accent} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h3 className="font-sub font-semibold text-lg mb-5 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /> Best sellers</h3>
          {data.top_items.length === 0 ? (
            <p className="text-slate-400 text-sm">No orders yet.</p>
          ) : (
            <div className="space-y-4">
              {data.top_items.map((it, i) => (
                <div key={it.name} data-testid={`top-item-${i}`}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="font-medium truncate">{i + 1}. {it.name}</span>
                    <span className="text-slate-400 font-mono-j">{it.qty} sold · {money(it.revenue, currency)}</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${(it.qty / maxQty) * 100}%`, backgroundColor: accent }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
